import * as monacoEditor from 'monaco-editor';
import * as React from 'react';

import {
  MonacoFile,
  createNewModel,
} from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import { useStudioEditor } from 'src/hooks/useStudioEditor';

interface ModelState {
  model: monacoEditor.editor.ITextModel;
  viewState: monacoEditor.editor.ICodeEditorViewState | null;
}

type UseStudioEditorOptions = Parameters<typeof useStudioEditor>['0'];

export function useTabbedStudioEditor({
  files,
  editorId,
  controlContents,
  selectedFile,
  setSelectedFile,
  getOverrideServices,
  getCodeEditorOpener,
  maintainScrollPosition,
  ...useStudioEditorOptions
}: Omit<
  UseStudioEditorOptions,
  'defaultValue' | 'overrideServices' | 'createDefaultModel' | 'language'
> & {
  files: Record<string, MonacoFile>;
  /**
   * A unique id for this editor. This is to avoid conflicts between multiple
   * tabbed editors creating models with the same uris
   */
  editorId: string;
  /**
   * Whether or not this is a controlled component, this decides if we update
   * the editor when content values change, or let the editor handle changes
   * itself (basically if edits will come from external updates or not)
   */
  controlContents: boolean;
  selectedFile: string | undefined;
  setSelectedFile: (selectedFileName: string) => void;
  getOverrideServices?: () => UseStudioEditorOptions['overrideServices'];
  getCodeEditorOpener?: (
    openFileRef: React.MutableRefObject<
      (fileName: string, range?: monacoEditor.IRange | undefined) => void
    >,
  ) => monacoEditor.editor.ICodeEditorOpener;
  maintainScrollPosition?: boolean;
}) {
  const [modelStatesByName, setModelStatesByName] = React.useState<
    Record<string, ModelState>
  >({});
  const [rangeToSelect, setRangeToSelect] = React.useState<
    monacoEditor.IRange | undefined
  >();

  // This will set the new selected file to open, and the range, update the stored viewState, and
  // highlight the opened range in the editor.
  const openFile = React.useCallback(
    (fileName: string, range?: monacoEditor.IRange) => {
      if (fileName) {
        setSelectedFile(fileName);
      }
      if (range) {
        setRangeToSelect(range);
      }
    },
    [setSelectedFile],
  );
  const openFileRef =
    React.useRef<
      (fileName: string, range?: monacoEditor.IRange | undefined) => void
    >(openFile);
  openFileRef.current = openFile;

  const codeEditorOpener = React.useMemo(
    () => getCodeEditorOpener?.(openFileRef),
    [getCodeEditorOpener],
  );

  React.useEffect(() => {
    const disposables: monacoEditor.IDisposable[] = [];
    if (codeEditorOpener) {
      disposables.push(
        monacoEditor.editor.registerEditorOpener(codeEditorOpener),
      );
    }
    return () => {
      disposables.forEach((disposable) => disposable.dispose());
    };
  }, [codeEditorOpener]);

  const overrideServices = React.useMemo(
    () => getOverrideServices?.(),
    [getOverrideServices],
  );
  const editor = useStudioEditor({
    ...useStudioEditorOptions,
    overrideServices,
    createDefaultModel: false,
    language:
      (selectedFile ? files[selectedFile]?.language : undefined) ??
      Object.values(files)[0]?.language ??
      'text',
  });

  const modelScrollTopByNameRef = React.useRef<Record<string, number>>({});
  const modelCursorSelectionByNameRef = React.useRef<
    Record<string, monacoEditor.Selection>
  >({});
  // Track scroll positions & selection of files, so that if maintainScrollPosition is true
  // we can scroll to the same position on reopen & put the cursor where the user expects
  React.useEffect(() => {
    if (maintainScrollPosition && selectedFile) {
      const disposables = [
        editor.onDidChangeModel(() => {
          const scrollTop = modelScrollTopByNameRef.current[selectedFile];
          if (scrollTop) {
            editor.setScrollTop(scrollTop);
          }
          const selection = modelCursorSelectionByNameRef.current[selectedFile];
          if (selection) {
            editor.setPosition(selection.getStartPosition());
            editor.focus();
            editor.setSelection(selection);
          }
        }),
        editor.onDidScrollChange((scrollEvent) => {
          modelScrollTopByNameRef.current[selectedFile] = scrollEvent.scrollTop;
        }),
        editor.onDidChangeCursorSelection((event) => {
          modelCursorSelectionByNameRef.current[selectedFile] = event.selection;
        }),
      ];
      return () => disposables.forEach((disposable) => disposable.dispose());
    }
  }, [editor, maintainScrollPosition, selectedFile]);

  // Catch all to make sure the selected file stays in sync with the editor.
  // This will open set the new selected file and update the stored viewState.
  React.useEffect(() => {
    const previousUri = editor.getModel()?.uri;
    const previousFile = previousUri?.path.slice(1);
    const currentFileModel = selectedFile && modelStatesByName[selectedFile];
    if (
      editor &&
      selectedFile &&
      currentFileModel &&
      previousFile !== selectedFile
    ) {
      if (previousFile) {
        const previousViewState = editor.saveViewState();
        const previousFileModel = modelStatesByName[previousFile];
        if (previousFileModel) {
          setModelStatesByName({
            ...modelStatesByName,
            [previousFile]: {
              ...previousFileModel,
              viewState: previousViewState,
            },
          });
        }
      }

      const { model, viewState } = currentFileModel;
      if (!model.isDisposed()) {
        editor.setModel(model);
        if (viewState) {
          editor.restoreViewState(viewState);
        }
      }
    }
  }, [editor, selectedFile, modelStatesByName]);

  // If a range has been selected to focus, apply it
  React.useEffect(() => {
    if (editor && rangeToSelect) {
      editor.setSelection(rangeToSelect);
      editor.revealRangeInCenter(rangeToSelect);
      editor.focus();
      setRangeToSelect(undefined);
    }
  }, [editor, rangeToSelect]);

  // When files or modelsByName is updated, sync the models to match the current files
  React.useEffect(() => {
    const modelNames = new Set(Object.keys(modelStatesByName));
    const fileNameSet = new Set(Object.keys(files));
    const missingFiles = Object.values(files).filter(
      (file) => !modelNames.has(file.id),
    );
    const extraModels = new Set(
      Object.keys(modelStatesByName).filter((value) => !fileNameSet.has(value)),
    );

    setModelStatesByName((currentModelsByName) => {
      if (controlContents) {
        Object.entries(currentModelsByName).forEach(
          ([serviceName, { model }]) => {
            if (
              fileNameSet.has(serviceName) &&
              files[serviceName]?.text !== model.getValue()
            ) {
              model.setValue(files[serviceName]?.text ?? '');
            }
          },
        );
      }

      if (!missingFiles.length && !extraModels.size) {
        return currentModelsByName;
      }

      return Object.fromEntries([
        ...Object.entries(currentModelsByName).filter(
          ([serviceName, { model }]) => {
            if (extraModels.has(serviceName)) {
              model?.dispose();
            }
            return !extraModels.has(serviceName);
          },
        ),
        ...missingFiles.map((file): [string, ModelState] => {
          const newModel = createNewModel(file, editorId);
          return [
            file.id,
            {
              model: newModel,
              viewState: null,
            },
          ];
        }),
      ]);
    });
  }, [controlContents, editorId, files, modelStatesByName]);

  // TODO reenable this working with fast refresh.
  //  I think what this requires is in the effect below, check for disposed
  // models and recreate them. We do to dispose here though right?
  // React.useEffect(() => {
  //   // return () =>
  //   //   monacoEditor.editor.getModels().forEach((model) => model?.dispose());
  //   // Object.values(modelsByName).forEach(({ model }) => model?.dispose());
  // }, []);

  const modelsByName = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(modelStatesByName).map(([key, value]) => [
          key,
          value.model,
        ]),
      ),
    [modelStatesByName],
  );

  return { editor, modelsByName };
}
